import { useEffect, useRef } from "react";
import Lenis from "lenis";

/**
 * Global smooth momentum scrolling powered by Lenis.
 * Also exposes `window.__lenis` so nav handlers can call
 * `window.__lenis.scrollTo(target, { offset, duration })`.
 */
export default function useLenis() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;
    // Expose globally so section links / CTAs can drive it.
    window.__lenis = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      if (window.__lenis === lenis) delete window.__lenis;
    };
  }, []);

  return lenisRef;
}

/** Smoothly scroll to a section id. Falls back to native scrollIntoView. */
export function smoothScrollTo(id, offset = -40) {
  const el = typeof id === "string" ? document.getElementById(id) : id;
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
